import { Loader2 } from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef } from "react";

import { Badge, Button, Card, CardHead, EmptyState } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { AdminSessionMessage } from "@/types";
import { messageFeedback } from "./messageFeedback";

function clockTime(value: string | null) {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * The conversation, with a "Trace 보기" toggle on every assistant answer.
 *
 * The toggle, not the answer, opens the trace: reading is the default here,
 * and selecting text in an answer should not rearrange the page. It says
 * whether its trace is the one open (`aria-pressed`). Every answer has one,
 * not only those carrying `llm_turn_id`: an answer that errored has no linked
 * turn, and "why is there no trace" is itself worth opening — the trace panel
 * explains it from the message's own metadata.
 */
export function ConversationThread({
  messages,
  loading,
  selectedId,
  assistantCount,
  clampUnselected,
  hasOlder,
  olderLoading,
  onLoadOlder,
  onSelect,
}: {
  messages: readonly AdminSessionMessage[];
  loading: boolean;
  selectedId: string | null;
  assistantCount: number;
  /**
   * Clamp the answers whose trace is not open. Only beside a trace: there the
   * thread is a narrow column, and a report-length answer would push the rest
   * of it out of reach. With the width to itself the thread reads in full.
   */
  clampUnselected: boolean;
  hasOlder: boolean;
  olderLoading: boolean;
  onLoadOlder: () => void;
  onSelect: (message: AdminSessionMessage) => void;
}) {
  // Opening or closing a trace changes the layout under the answer: the
  // thread becomes its own scroller (starting at the top) or hands scrolling
  // back to the page. Either way the answer acted on would leave the screen,
  // so it is brought back — the closed one too, which is why the last id is
  // kept. Before paint, so it never visibly jumps.
  const listRef = useRef<HTMLOListElement>(null);
  const anchorId = useRef<string | null>(null);
  useLayoutEffect(() => {
    const id = selectedId ?? anchorId.current;
    anchorId.current = id;
    if (!id || loading) return;
    listRef.current
      ?.querySelector(`[data-message-id="${id}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedId, loading]);

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHead
        title="대화"
        hint={
          assistantCount > 0
            ? "assistant 답변의 Trace 보기로 그 답변의 실행 기록을 연다"
            : "assistant 답변이 없어 열 Trace 가 없다"
        }
      >
        {hasOlder && (
          <Button variant="outline" size="sm" onClick={onLoadOlder} disabled={olderLoading}>
            {olderLoading && <Loader2 className="animate-spin" />}
            이전 메시지 더 보기
          </Button>
        )}
      </CardHead>

      {loading ? (
        <p className="p-4 text-sm text-muted-foreground">불러오는 중…</p>
      ) : messages.length === 0 ? (
        <EmptyState title="메시지가 없습니다." body="이 세션에는 표시할 메시지가 없습니다." />
      ) : (
        <ol ref={listRef} className="grid grid-cols-1 gap-2 p-3.5">
          {/* `grid-cols-1` is `minmax(0, 1fr)`, and the text breaks anywhere:
              an implicit track grows to its widest unbreakable line, so a
              pasted URL pushed the answer — and its Trace toggle — past the
              card's clipped edge. */}
          {messages.map((message) => (
            <li key={message.chat_message_id} data-message-id={message.chat_message_id}>
              {message.role === "assistant" ? (
                <AssistantMessage
                  message={message}
                  selected={message.chat_message_id === selectedId}
                  clamped={clampUnselected && message.chat_message_id !== selectedId}
                  onSelect={() => onSelect(message)}
                />
              ) : (
                <div className="rounded-r-lg border border-l-[3px] border-border border-l-border bg-surface-subtle px-3 py-2.5">
                  <MessageHead message={message} />
                  <p className="text-[13px] whitespace-pre-wrap wrap-anywhere">{message.content}</p>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function AssistantMessage({
  message,
  selected,
  clamped,
  onSelect,
}: {
  message: AdminSessionMessage;
  selected: boolean;
  clamped: boolean;
  onSelect: () => void;
}) {
  const feedback = messageFeedback(message);
  const commented = feedback.down.filter((entry) => entry.comment);

  return (
    <div
      className={cn(
        "rounded-r-lg border border-l-[3px] border-border border-l-primary bg-card px-3 py-2.5 transition-colors",
        selected && "bg-accent",
      )}
    >
      <MessageHead
        message={message}
        action={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={selected}
            onClick={onSelect}
            className="min-h-6 px-2 py-0.5 text-xs"
          >
            {selected ? "Trace 닫기" : "Trace 보기"}
          </Button>
        }
      >
        {feedback.down.length > 0 && (
          <Badge
            size="compact"
            className="border-destructive-line bg-destructive-surface text-destructive"
            title={feedback.down.map((entry) => entry.email).join(", ")}
          >
            👎{feedback.down.length > 1 ? ` ${feedback.down.length}` : ""}
          </Badge>
        )}
        {feedback.up.length > 0 && (
          <Badge
            size="compact"
            className="border-success-line bg-success-surface text-success"
            title={feedback.up.map((entry) => entry.email).join(", ")}
          >
            👍{feedback.up.length > 1 ? ` ${feedback.up.length}` : ""}
          </Badge>
        )}
      </MessageHead>
      <p className={cn("text-[13px] whitespace-pre-wrap wrap-anywhere", clamped && "line-clamp-6")}>
        {message.content || <span className="text-muted-foreground">(빈 답변)</span>}
      </p>
      {commented.length > 0 && (
        <div className="mt-2 grid gap-1 border-t border-dashed border-border pt-2 text-xs text-destructive wrap-anywhere">
          {commented.map((entry) => (
            <p key={entry.email}>
              👎 {entry.comment}
              <span className="ml-1.5 text-muted-foreground">— {entry.email}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageHead({
  message,
  action,
  children,
}: {
  message: AdminSessionMessage;
  /** At the far end, after the time. */
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <span className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-[10px] tracking-wider text-muted-foreground uppercase">
        {message.role}
      </span>
      {message.sender_email && (
        <span className="text-[11px] text-muted-foreground">{message.sender_email}</span>
      )}
      {children}
      {message.visibility !== "normal" && <Badge size="compact">{message.visibility}</Badge>}
      {message.deleted_at && <Badge size="compact">deleted</Badge>}
      <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
        {clockTime(message.created_at)}
      </span>
      {action}
    </span>
  );
}
